import React, { Component } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { Form, Radio, Select, Popover, Button, Dropdown, Menu, notification } from "antd";
const Option = Select.Option;
const FormItem = Form.Item;
import { SettingOutlined, ExportOutlined } from "@ant-design/icons";
import { DiscreteColorLegend } from "@unifiedpatents/euclid-react";
import { groupedCountToPercent, orderBy } from "@unifiedpatents/euclid-react/lib/helpers/preprocessors";
import { isString } from "lodash";
import { csvDownload, imageDownload } from "../../helpers/dom";

export const withChartConfig = (WrappedComponent, defaultConfigs = {}) => {
  return class ChartConfigHOC extends Component {
    static propTypes = {
      title: PropTypes.string,
      legends: PropTypes.arrayOf(PropTypes.string),
      scrollable: PropTypes.bool,
      dataFormatter: PropTypes.func,
      tooltipValueFormat: PropTypes.func,
      orientation: PropTypes.oneOf(["vertical", "horizontal"]),
      tickFormatX: PropTypes.func,
      tickFormatY: PropTypes.func,
      colorMap: PropTypes.object,
      exportable: PropTypes.bool
    };

    static defaultProps = {
      dataFormatter: d => d,
      scrollable: true,
      exportable: true
    };

    state = {
      currentShow: defaultConfigs.show,
      currentSort: defaultConfigs.sort,
      data: defaultConfigs.data
    };

    render() {
      const { title, dataFormatter, legends, scrollable, tooltipValueFormat, orientation, tickFormatX, tickFormatY, colorMap, exportable, ...rest } = this.props;
      const { currentShow, currentSort, data } = this.state;
      const showCSVDownloadOption = this.isValidCSVData(data);
      const currentDataFormatter =
        currentShow === "percentage"
          ? currentSort
            ? d => orderBy(groupedCountToPercent(dataFormatter(d)), legends, currentSort)
            : d => orderBy(groupedCountToPercent(dataFormatter(d)), legends)
          : currentSort
            ? d => orderBy(dataFormatter(d), legends, currentSort)
            : d => orderBy(dataFormatter(d), legends);
      const toPct = v => `${(v * 100).toFixed(1)}%`,
        toPctBare = v => `${(v * 100).toFixed(0)}%`;
      const currentTooltipValueFormat = tooltipValueFormat ? tooltipValueFormat : currentShow === "percentage" ? toPct : undefined;
      const currentTickFormatX = tickFormatX ? tickFormatX : currentShow === "percentage" && orientation === "vertical" ? toPctBare : undefined;
      const currentTickFormatY = tickFormatY ? tickFormatY : currentShow === "percentage" && orientation !== "vertical" ? toPctBare : undefined;
      const currentZeroAxisIndex = legends && legends.length ? (legends.indexOf(currentSort) >= 0 ? legends.indexOf(currentSort) : 0) : 0;
      const configs = (
        <Form layout="inline">
          {currentShow ? (
            <FormItem label="Show">
              <Radio.Group value={currentShow} size="small" onChange={e => this.setState({ currentShow: e.target.value })}>
                <Radio.Button value="count">{"Count"}</Radio.Button>
                <Radio.Button value="percentage">{"Percentage"}</Radio.Button>
              </Radio.Group>
            </FormItem>
          ) : null}
          {currentSort && isString(currentSort) ? (
            <FormItem label="Sort">
              <Select
                value={currentSort}
                size="small"
                placeholder="Sort By"
                onChange={val => this.setState({ currentSort: val })}
                getPopupContainer={trigger => trigger.parentNode}
                style={{ width: 180 }}
              >
                {legends &&
                  legends.map(legend => (
                    <Option value={legend} key={legend}>
                      {legend}
                    </Option>
                  ))}
                <Option value="__total__" key="total">
                  {"Total"}
                </Option>
              </Select>
            </FormItem>
          ) : null}
        </Form>
      );
      const menu = (
        <Menu onClick={e => this.handleDownload(e, "chart")}>
          <Menu.Item key="png">Export chart as PNG</Menu.Item>
          {showCSVDownloadOption && <Menu.Item key="csv">Export chart data as CSV</Menu.Item>}
        </Menu>
      );
      return (
        <div style={scrollable ? { width: "100%", textAlign: "center", minWidth: 435 } : { width: "100%", textAlign: "center" }}>
          <div style={scrollable ? { position: "relative" } : { position: "relative", width: "100%" }}>
            <div style={{ position: "absolute", right: 0, top: 0 }}>
              {data && exportable && (
                <Dropdown overlay={menu} placement="bottomRight">
                  <Button icon={<ExportOutlined />} shape="circle" style={{ float: "right", marginRight: 8, zIndex: 2 }} />
                </Dropdown>
              )}
              {(currentShow || currentSort) && (
                <Popover placement="bottomRight" content={configs} trigger="click">
                  <Button icon={<SettingOutlined />} shape="circle" style={{ float: "right", marginRight: 8, zIndex: 2 }} />
                </Popover>
              )}
            </div>
            <div ref={"chart"}>
              <WrappedComponent
                {...rest}
                title={title}
                orientation={orientation}
                dataFormatter={currentDataFormatter}
                zeroAxisIndex={currentZeroAxisIndex}
                tickFormatX={currentTickFormatX}
                tickFormatY={currentTickFormatY}
                tooltipValueFormat={currentTooltipValueFormat}
                colorMap={colorMap}
                onDataReady={data => this.setState({ data })}
                key={[currentShow, currentSort]}
              />
              {legends && legends.length > 0 && <DiscreteColorLegend orientation="horizontal" items={legends.map(key => ({ title: key, color: colorMap[key] }))} />}
            </div>
          </div>
        </div>
      );
    }

    isSingleLayerData = data => {
      let isSingleLayer = true;
      for (let i = 0; i < data.length; i++) {
        const { v } = data[i];
        if (v && typeof v === "object") {
          isSingleLayer = false;
          break;
        }
      }
      return isSingleLayer;
    };

    // Only data like [k,v] or [k,v:[{k,v}]] can be exported as csv
    isValidCSVData = data => {
      let showCSVDownloadOption = false;
      if (data && Array.isArray(data) && data.length > 0) {
        if (this.isSingleLayerData(data)) {
          showCSVDownloadOption = true;
        } else {
          const secondLayerData = [];
          data.forEach(d => secondLayerData.push(d.v));
          let isSecondLayer = true;
          for (let i = 0; i < secondLayerData.length; i++) {
            const secondData = secondLayerData[i];
            if (!this.isSingleLayerData(secondData)) {
              isSecondLayer = false;
              break;
            }
          }
          if (isSecondLayer) {
            showCSVDownloadOption = true;
          }
        }
      }
      return showCSVDownloadOption;
    };

    handleDownload = (e, id) => {
      const { key } = e;
      const { title } = this.props;
      const { data } = this.state;
      if (key === "png") {
        const imgRef = ReactDOM.findDOMNode(this.refs[id]);
        imageDownload(imgRef, title).catch(() => {
          notification.error({
            message: "Failed to export image.",
            key: "image-download-error"
          });
        });
      } else if (key === "csv") {
        this.saveCSV(data, title);
      }
    };

    saveCSV = (data, name) => {
      const isSingleLayer = this.isSingleLayerData(data);
      const fields = isSingleLayer ? ["key", "value"] : ["key"];
      const formattedData = [];
      if (isSingleLayer) {
        data.forEach(({ k: key, v: value }) => formattedData.push({ key, value }));
      } else {
        const items = [];
        data.forEach(({ k: key, v: value }) => {
          fields.push(key);
          value.forEach(({ k: k2 }) => {
            if (items.indexOf(k2) === -1) {
              items.push(k2);
            }
          });
        });
        items.forEach(item => {
          let obj = { key: item };
          data.forEach(({ k: key, v: value }) => {
            value.forEach(({ k: k2, v: v2 }) => {
              if (k2 === item) {
                obj[key] = v2;
              }
            });
          });
          formattedData.push(obj);
        });
      }
      const fileName = name ? `${name}.csv` : "chart.csv";
      csvDownload(formattedData, fields, fileName).catch(() => {
        notification.error({
          message: "Failed to export CSV.",
          key: "csv-download-error"
        });
      });
    };
  };
};
